// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { xCreateAccountUpdate } from "@electrowire/App/Handlers/XNG/CreateAccount";
import { CustomAppState } from "@electrowire/App/Store/Reducers";
import PopupContextProvider from "@gcg/App/Components/XPopup/XContext";
import XPopup from "@gcg/App/Components/XPopup/XPopup";
import { screen } from "@gcg/XDL/Helpers";
import Info from "@gcg/XDL/Icons/Info";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapDispatchToProps = {
    xCreateAccountUpdate,
    signInAsGuest,
};

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const accountSettings = getSettingsCollection(state).accountSettings;
    const returnUrl = getReturnUrl(state);
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    const applicationCookie = getCookie(".AspNet.ApplicationCookie");
    const referredFromShipping = returnUrl === shippingPageUrl;

    return {
        allowGuestCheckout:
            accountSettings.allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (!state.context.session.isAuthenticated || !applicationCookie) &&
            referredFromShipping,
        disableNewB2BRegistration:
            (getSettingsCollection(state).customSettings as any).disableNewB2BRegistration === "True",
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
        createAccountState: (state as CustomAppState).xCreateAccount.createAccountState,
        returnUrl,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CreateAccountSignInStyles {
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    step1Wrapper?: InjectableCss;
    tooltipWrapper?: InjectableCss;
    infoTipIconStyle?: InjectableCss;
    nextButtonWrapperStyles?: InjectableCss;
    optionWrapper?: InjectableCss;
    radioGroup?: RadioGroupPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
}

export const CreateAccountSignInStyles: CreateAccountSignInStyles = {};

const styles = CreateAccountSignInStyles;

const CreateAccountSignIn: FC<Props> = ({
    createAccountState,
    allowGuestCheckout,
    id,
    disableNewB2BRegistration,
    createAccountUrl,
    history,
    xCreateAccountUpdate,
    signInAsGuest,
    returnUrl,
}) => {
    const [value, setValue] = useState("existing");

    const createAnAccountHandler = (e: any) => {
        setValue(e.target.value);
    };

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }
        if (value) {
            history.push(createAccountUrl);
            xCreateAccountUpdate({ createAccountState: value });
        }
    };
    const options: any = [
        {
            value: "existing",
            labelMessage: siteMessage("Custom_AccountRegistration_Text_Option_1"),
            infoMessage: siteMessage("Custom_AccountRegistration_InfoText_Option_1"),
        },
    ];

    if (!disableNewB2BRegistration) {
        options.push({
            value: "newB2B",
            labelMessage: siteMessage("Custom_AccountRegistration_Text_Option_2"),
            infoMessage: siteMessage("Custom_AccountRegistration_InfoText_Option_2"),
        });
    }

    options.push({
        value: "newB2C",
        labelMessage: siteMessage("Custom_AccountRegistration_Text_Option_3"),
        infoMessage: siteMessage("Custom_AccountRegistration_InfoText_Option_3"),
    });

    return (
        <>
            <StyledWrapper {...styles.step1Wrapper}>
                <Zone contentId={id} zoneName="createAccountContent" />
                <RadioGroup value={value} onChangeHandler={createAnAccountHandler} {...styles.radioGroup}>
                    {options.map((option: any) => {
                        return (
                            <StyledWrapper key={option.value} {...styles.optionWrapper}>
                                <Radio value={option.value}>{option.labelMessage}</Radio>
                                {option.infoMessage.toString().length > 0 && (
                                    <StyledWrapper {...styles.tooltipWrapper}>
                                        <PopupContextProvider>
                                            <XPopup
                                                type="tooltip"
                                                trigger={
                                                    <StyledWrapper>
                                                        <Icon
                                                            {...styles.infoTipIconStyle}
                                                            src={Info}
                                                            size={14}
                                                            color="secondary"
                                                        />
                                                    </StyledWrapper>
                                                }
                                                position="top center"
                                            >
                                                <StyledWrapper className="popup-content-wrapper">
                                                    {option.infoMessage}
                                                </StyledWrapper>
                                            </XPopup>
                                        </PopupContextProvider>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        );
                    })}
                </RadioGroup>
                <StyledWrapper {...styles.nextButtonWrapperStyles}>
                    {allowGuestCheckout && (
                        <Button
                            {...styles.checkoutAsGuestButton}
                            onClick={handleCheckoutAsGuest}
                            data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                        >
                            {translate("Checkout as Guest")}
                        </Button>
                    )}
                    {createAccountUrl && (
                        <Button
                            {...styles.createNewAccountButton}
                            onClick={handleCreateNewAccount}
                            data-test-selector="signInCreateNewAccount_createNewAccount"
                        >
                            {translate("Create Account")}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(CreateAccountSignIn)),
    definition: {
        allowedContexts: ["SignInPage"],
        group: "Sign In",
        displayName: "Creat Account Sign In",
    },
};

export default widgetModule;
